/**
 * One collapsible section on the Maintenance page (Full Outages, Camera-
 * faulted, Scanner-faulted, Maintenance Requests, Powered Down, Stacked).
 * Extracted from OutagesPage; same shape it always had.
 *
 * Mobile behavior: empty sections start collapsed (just the header strip);
 * populated sections start open. Tapping the header toggles. On md:+ the
 * section is always fully visible — desktop has the vertical room and
 * operators want to scan across sections without extra clicks.
 */
import React, { useState } from "react";

import {
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../ResponsiveDataTable";
import StateBadge from "../StateBadge";
import type { Box } from "../../types/boxes";

interface SectionProps {
  title: string;
  subtitle: string;
  accentBorder: string;
  accentText: string;
  rows: Box[];
  emptyMessage: string;
  columns: ResponsiveColumn<Box>[];
}

export const Section: React.FC<SectionProps> = ({
  title,
  subtitle,
  accentBorder,
  accentText,
  rows,
  emptyMessage,
  columns,
}) => {
  // Mobile-only collapse: empty sections default closed (just take up the
  // header strip); populated sections default open. Tapping the header
  // toggles on phones. On md+ the section is always fully visible —
  // desktop has the vertical room and operators want to scan across
  // sections without extra clicks.
  const [isCollapsed, setIsCollapsed] = useState(rows.length === 0);
  return (
    <section>
      <button
        type="button"
        onClick={() => setIsCollapsed((v) => !v)}
        aria-expanded={!isCollapsed}
        className={`w-full md:cursor-default md:pointer-events-none flex items-baseline gap-3 pb-1 border-b-2 ${accentBorder} text-left`}
      >
        <span
          aria-hidden="true"
          className={`md:hidden text-base ${accentText} transition-transform inline-block ${
            isCollapsed ? "" : "rotate-90"
          }`}
        >
          ▸
        </span>
        <h2 className={`text-base font-bold ${accentText}`}>
          {title} ({rows.length})
        </h2>
        <span className="text-xs text-secondary-500 hidden md:inline">
          {subtitle}
        </span>
      </button>
      {/* Subtitle on mobile lives below the header so it doesn't crowd the
          tap-target row. Hidden when section is collapsed. */}
      <p
        className={`md:hidden text-xs text-secondary-500 mt-1 ${
          isCollapsed ? "hidden" : ""
        }`}
      >
        {subtitle}
      </p>
      {/* Body — collapsible on mobile, always shown on md+. */}
      <div className={isCollapsed ? "hidden md:block" : "block"}>
        {rows.length === 0 ? (
          <div className="mt-3 text-sm text-secondary-500 bg-white p-4 rounded-md border border-secondary-200">
            {emptyMessage}
          </div>
        ) : (
          <div className="mt-3">
            <ResponsiveDataTable
              columns={columns}
              rows={rows}
              getRowKey={(b) => b.scu_name}
              mobileHeader={(b) => (
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono text-xs font-semibold">
                    {b.scu_name}
                  </code>
                  <StateBadge state={b.state} />
                </div>
              )}
            />
          </div>
        )}
      </div>
    </section>
  );
};


export default Section;
